import React, { useEffect, useState } from "react";
import axios from "axios";
import { AlertCircle, Loader2 } from "lucide-react";
import Loading from "../Components/Loading";

export default function HarmonizedSystemOfNomenclature({
  exportingCountry,
  destinationCountry,
  businessDetails,
  products,
  selectedState,
  selectedDistrict,
  keywords,
}) {
  const [loading, setLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState("");
  const [productInfo, setProductInfo] = useState(null);
  const [selectedHSN, setSelectedHSN] = useState(null);
  const [selectedGST, setSelectedGST] = useState(null);
  const [itemSelected, setItemSelected] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const query = `${products} ${keywords}`;
        const response = await axios.post(
          "https://cross-intelligence-50023657941.development.catalystappsail.in/hsn-gst-detail",
          { query }
        );
        setData(response.data);
        sessionStorage.setItem("hsnGstData", JSON.stringify(response.data));
        setLoading(false);
        fetchSummary(response.data);
      } catch (error) {
        console.error("Error:", error);
        setLoading(false);
      }
    };

    const fetchSummary = async (hsnData) => {
      setSummaryLoading(true);
      try {
        const tableData = hsnData.map(item => 
          `HSN: ${item.HSN}, Description: ${item.description}, GST: ${!item.GST || item.GST === "" ? "Free" : item.GST}`
        ).join("\n");

        const response = await axios.post("https://cross-intelligence-50023657941.development.catalystappsail.in/genarate-summary", {
          prompt: `Think like you are an international HSN code finder and GST Details provider for India. Answer the following question from the available data:
          What all the HSN code and GST can apply for ${businessDetails} ${products} ${keywords}? Use the below reference:
          ${tableData}`,
          hsnData,
        });
        if (response.data && response.data.anwser) {
          setSummary(response.data.anwser); // Fixed the key
          sessionStorage.setItem("hsnGstSummary", response.data.anwser);
        } else {
          console.error("Unexpected response format:", response.data);
        }
      } catch (error) {
        console.error("Error fetching summary:", error);
      } finally {
        setSummaryLoading(false);
      }
    };

    const storedData = sessionStorage.getItem("hsnGstData");
    const storedSummary = sessionStorage.getItem("hsnGstSummary");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      setData(parsedData);
      setLoading(false);
      if (storedSummary) {
        setSummary(storedSummary);
      } else {
        fetchSummary(parsedData);
      }
    } else {
      fetchData();
    }
  }, [products, keywords, businessDetails]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 flex justify-center">
      <div className="w-full max-w-5xl">
        <h1 className="text-3xl font-bold text-center mb-8">HSN GST Details</h1>
        {summaryLoading ? (
          <div className="flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        ) : (
          summary && (
            <div className="bg-gray-100 p-4 rounded-md shadow-md mb-4">
              <p className="text-lg font-medium">
                {summary.split(/(\*\*.*?\*\*)/).map((part, index) =>
                  part.startsWith("**") && part.endsWith("**") ? (
                    <strong key={index}>{part.slice(2, -2)}</strong>
                  ) : (
                    part
                  )
                )}
              </p>
            </div>
          )
        )}

        <div className="grid grid-cols-1 gap-8">
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <h2 className="text-xl font-semibold bg-gray-100 p-4">
              HSN Details
            </h2>
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    HSN
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Description
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    GST Rate
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data && data.map((item) => (
                  <tr
                    key={item._id}
                    className="hover:bg-gray-50 cursor-pointer transition duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.HSN}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 break-words">
                      {item.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          !item.GST || item.GST === ""
                            ? "bg-green-100 text-green-800"
                            : "bg-blue-100 text-blue-800"
                        }`}
                      >
                        {!item.GST || item.GST === "" ? "Free" : item.GST}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}